import React, { useEffect, useRef, useState } from "react";
import ShowIfSection from "../ShowIfSection";
import "./SubsectionProperties.scss";

/**
 * SubsectionProperties
 * Renders property editors for selected subsection (name and color)
 * @author Sarthak Arora
 */
const SubsectionProperties = ({ subsection, onChange, handleShowIfChange }) => {
  const [color, setColor] = useState(subsection.color || "#ffffff");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [name, setName] = useState(subsection.title || "");
  const dropdownRef = useRef(null);

  // Update local color state when subsection changes
  useEffect(() => {
    setColor(subsection.color || "#ffffff");
  }, [subsection.color]);

  // Update local name state when subsection changes
  useEffect(() => {
    setName(subsection.title || "");
  }, [subsection.title]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showColorPicker]);
  if (!subsection) {
    return (
      <div className='no-selection p-4 text-center'>
        <span>Select a subsection to edit its properties</span>
      </div>
    );
  }

  // Kendo ColorPicker palette settings
  const paletteSettings = {
    palette: [
      "#FFF7F7",
      "#FFEAEA",
      "#FFDCDC",
      "#FFCECE",
      "#FFC0C0",
      "#FFB3B3",
      "#FFF4FA",
      "#FFE9F3",
      "#FFDDEE",
      "#FFCFE7",
      "#FFBFE0",
      "#FFAFD8",
      "#FFF9F1",
      "#FFF1E0",
      "#FFE9D1",
      "#FFE1C2",
      "#FFD9B3",
      "#FFCCA3",
      "#FFFDF4",
      "#FFF9E8",
      "#FFF3D9",
      "#FFEFC9",
      "#FFE9B8",
      "#FFE3A8",
      "#F8FFF8",
      "#EDFAED",
      "#E0F5E0",
      "#D2EFD2",
      "#C4E9C4",
      "#B8E3B8",
      "#F7FBFF",
      "#ECF5FF",
      "#E1EFFF",
      "#D5E8FF",
      "#CAE2FF",
      "#C0DCFF",
      "#FFFFFF",
      "#F8F8F8",
      "#F0F0F0",
      "#E6E6E6",
      "#DCDCDC",
      "#D3D3D3",
    ],
    columns: 6,
    tileSize: 28,
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName); // Update local state immediately
    onChange?.(subsection.id, { title: newName }); // Sync with parent
  };

  const handleColorChange = (selectedColor) => {
    onChange?.(subsection.id, { color: selectedColor });
    setColor(selectedColor);
    setShowColorPicker(false);
  };

  const handleColorDisplayClick = () => {
    setShowColorPicker(!showColorPicker);
  };

  return (
    <div className='subsection-properties-container p-3'>
      <div className='property-group mb-4'>
        <label className='d-block mb-2'>Subsection Name</label>
        <input
          max={30}
          type='text'
          placeholder='Enter subsection name'
          value={name}
          onChange={handleNameChange}
          maxLength={50}
          className='form-control'
        />
      </div>

      <div className='property-group mb-4'>
        <label className='d-block mb-2'>Background Color</label>
        <div className='color-picker-wrapper position-relative' ref={dropdownRef}>
          <div className='color-display d-flex align-items-center' onClick={handleColorDisplayClick}>
            <div className='color-preview me-2 flex-shrink-0' style={{ backgroundColor: color }}></div>
            <svg className={`dropdown-arrow flex-shrink-0 ${showColorPicker ? "rotated" : ""}`} width='16' height='16' viewBox='0 0 24 24'>
              <path d='M7 10l5 5 5-5z' fill='currentColor' />
            </svg>
          </div>

          {/* Color Palette dropdown */}
          {showColorPicker && (
            <div className='color-picker-dropdown position-absolute mt-2 p-3'>
              <div className='color-palette-grid'>
                {paletteSettings.palette.map((colorHex, index) => (
                  <div
                    key={index}
                    className={`palette-tile ${color === colorHex ? "selected" : ""}`}
                    style={{ backgroundColor: colorHex }}
                    onClick={() => handleColorChange(colorHex)}
                    title={colorHex}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ShowIfSection // Show If Section
        selectedSubsection={subsection}
        handleShowIfChange={handleShowIfChange}
      />
    </div>
  );
};

export default SubsectionProperties;
